import { useEffect, useRef, useState } from 'react';
import { Icon } from '../icons/Icon';
import { IconButton, type IconButtonProps } from './IconButton';

export interface CopyButtonProps extends Omit<IconButtonProps, 'icon' | 'aria-label' | 'onClick'> {
  /** Text placed on the clipboard. */
  value: string;
  /** What is being copied, for the accessible name — "Copy phone number". */
  label?: string;
}

/**
 * Copy-to-clipboard button that confirms itself: the icon flips to a tick for a
 * moment after a successful write. Anything worth showing as `mono` text — a
 * phone number, a dial code — should have one of these next to it, because the
 * alternative is the owner transcribing digits by hand.
 */
export function CopyButton({ value, label = 'Copy', size = 'sm', ...rest }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  // Don't set state on an unmounted button if the panel closes mid-flash.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <IconButton
      size={size}
      aria-label={copied ? `${label} — copied` : label}
      icon={<Icon name={copied ? 'check' : 'copy'} size={16} />}
      onClick={() => {
        // Insecure origins and denied permissions both reject here; staying
        // silent is right — the value is on screen and selectable either way.
        void navigator.clipboard?.writeText(value).then(
          () => {
            setCopied(true);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 1600);
          },
          () => {},
        );
      }}
      {...rest}
    />
  );
}
